/**
 * googleMapsLoader – singleton promise-based dynamic loader for the Google Maps
 * JavaScript SDK (libraries: places).
 *
 * Uses the JS SDK rather than the REST API so that browser CORS restrictions
 * are handled transparently by Google (the same API key works in both the
 * mobile app's REST calls and here).
 *
 * Mirrors the mobile app's GOOGLE_API_KEY constant from location_service.dart.
 */

// Falls back to the hardcoded key used in the mobile app if no env var is set.
export const GOOGLE_MAPS_API_KEY: string =
  (import.meta as unknown as { env?: Record<string, string> }).env?.VITE_GOOGLE_MAPS_KEY ??
  'AIzaSyCUI63Fy2KKTnOUCdO5_tnrjisaXIz7ZQM';

// Singleton promise – resolved once the SDK is loaded and ready.
let _loadPromise: Promise<void> | null = null;

/**
 * Dynamically injects the Maps JS SDK <script> tag and resolves when
 * `window.google.maps.places` is available.
 * Subsequent calls return the same promise (no duplicate tags).
 */
export function loadGoogleMapsScript(): Promise<void> {
  // Already loaded
  if (
    typeof window !== 'undefined' &&
    (window as unknown as { google?: { maps?: unknown } }).google?.maps
  ) {
    return Promise.resolve();
  }

  // Already loading
  if (_loadPromise) return _loadPromise;

  _loadPromise = new Promise<void>((resolve, reject) => {
    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAPS_API_KEY}&libraries=places`;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve();
    script.onerror = () => {
      _loadPromise = null; // allow retry on error
      reject(new Error('Failed to load Google Maps SDK. Check your API key.'));
    };
    document.head.appendChild(script);
  });

  return _loadPromise;
}
